import React from 'react'
import { UNSAFE_NavigationContext } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import useFileUploadComplete from '../upload/hooks/useFileUploadComplete'
import useCheckIfFileIsProfilling from '../upload/hooks/useCheckIfFileIsProfiling'
import ShareProjectDialog from './shareProject/shareProjectDialog'
import InputQuestions from './InputQuestions'
import FileList from './FileList'
import mappingApi from '../../store/objects/mappingApi'
import { changedAction, setEdpUploadingStatusAction } from '../../store/pages/project/actions'
import { clearPreCalState } from '../../store/pages/preCalculation'
import { styled } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'

import {
  getAccess,
  createProject,
  updateProject
} from '../../store/pages/project/async'

const INITIAL_REQUIRED_FIELDS = [
  'country',
  'product',
  'studyType',
  'isPII',
  'isTestData',
  'projectName',
  'clientName',
  'projectDate'
]

const FILE_TYPES_SKIPMAP_REQUIRED = ['I', 'T']

const StyledPaper = styled(Paper)(({
  padding: '8px',
  width: '100%',
  backgroundColor: '#DEDEDEAB'
}))

const StyledGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2)
}))

const useNavigationBlock = (shouldBlock) => {
  const navigator = React.useContext(UNSAFE_NavigationContext).navigator
  React.useEffect(() => {
    if (!shouldBlock) return

    const originalPush = navigator.push
    const originalReplace = navigator.replace

    navigator.push = (...args) => {
      const proceed = window.confirm('You have unsaved changes. Are you sure you want to leave this page?')
      if (proceed) originalPush(...args)
    }

    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
    }
  }, [navigator, shouldBlock])
}

const allSkipMapRequiredFilesSelected = (files) => {
  return FILE_TYPES_SKIPMAP_REQUIRED.every(requiredType => {
    return files.some(file => file.type === requiredType && file.edpStatus !== 'failed')
  })
}

export default function Upload () {
  useCheckIfFileIsProfilling()
  useFileUploadComplete()

  const dispatch = useDispatch()
  const user = useSelector(store => store.user)
  const project = useSelector(store => store.project)
  const { unsavedChanges } = project
  useNavigationBlock(unsavedChanges)
  const { isLoaded, isSaving } = useSelector(store => store.global)
  const isExistingProjectFabric = project.files.some(file => file.src === 'dataFabric')
  const isExistingProjectEdp = project.files.some(file => file.src === 'edp')
  const isExistingProjectCsv = project.files.some(file => file.format === 'csv')
  dispatch({ ...clearPreCalState })

  const [openShareProject, setOpenShareProject] = React.useState(false)

  const [source, setSource] = React.useState({
    csv: isExistingProjectCsv,
    fabric: isExistingProjectFabric,
    edp: isExistingProjectEdp
  })

  const access = getAccess(
    project.sharedWithIds,
    project.ownerId,
    project.studyType,
    user.role,
    user.ownerId,
    'UPLOAD'
  )

  const REQUIRED_FIELDS = project.studyType === 'redi' ? [...INITIAL_REQUIRED_FIELDS, 'calcIBNR'] : INITIAL_REQUIRED_FIELDS.filter(required => required !== 'calcIBNR')

  const doFilesExist = project.files.length > 0
  const allRequiredFieldsFilled = REQUIRED_FIELDS.every(key => project[key] !== '')

  const isEdp = project.files.some(file => file.src === 'edp')
  const edpFiles = project.files.filter(file => file.src === 'edp')
  const isEdpFilesUploaded = edpFiles.some(file => file.edpStatus === 'created')

  const allFileTypesSelected = project.studyType === 'skipMap'
    ? allSkipMapRequiredFilesSelected(project.files)
    : project.files.every(file => file.fileType)

  const valid = doFilesExist && allFileTypesSelected && allRequiredFieldsFilled
  const tooltip = valid ? 'Save Project' : 'Required Fields Missing'

  const handleStudyTypeChange = React.useCallback((option, event) => {
    mappingApi.removeMappings(project.id)
    dispatch({
      ...changedAction,
      payload: {
        key: event.name,
        value: option.value
      }
    })
    setSource({
      csv: false,
      fabric: false,
      edp: false
    })
  }, [
    project.id,
    dispatch
  ])

  const handleSourceChange = (event) => {
    const { name, checked } = event.target
    setSource({
      csv: name === 'csv' ? checked : false,
      fabric: name === 'fabric' ? checked : false,
      edp: name === 'edp' ? checked : false
    })
  }

  const handleOpenDialog = React.useCallback(() => {
    setOpenShareProject(true)
  }, [setOpenShareProject])

  const handleCloseDialog = React.useCallback(() => {
    setOpenShareProject(false)
  }, [setOpenShareProject])

  const handleSave = React.useCallback(() => {
    if (project.id) {
      if (isEdp && isEdpFilesUploaded) {
        dispatch({
          ...setEdpUploadingStatusAction,
          payload: project.files
        })
      }

      dispatch(updateProject())
    } else {
      if (isEdp && isEdpFilesUploaded) {
        dispatch({
          ...setEdpUploadingStatusAction,
          payload: project.files
        })
      }

      dispatch(createProject())
    }
  }, [project.id, project.files, dispatch, isEdp, isEdpFilesUploaded])

  if (!isLoaded) {
    return (<div></div>)
  }

  return (
    <StyledPaper
      data-testid='root'
      elevation={1}
    >
      <Grid container direction="column">
        <Grid item xs={12}>
          <InputQuestions
            data-testid='inputs'
            onOpen={handleOpenDialog}
            handleSelectChange={handleStudyTypeChange}
          />
        </Grid>

        <StyledGrid item xs={12}>
          <FileList data-testid='source' source={source} handleChange={handleSourceChange}/>
        </StyledGrid>

        {access && (
          <Grid item xs={12}>
            {isSaving
              ? <CircularProgress
                data-testid='progress-saving'
              />
              : <Tooltip
                data-testid='tooltip'
                title={tooltip}
              >
                <span>
                  <Button
                    data-testid='button-save'
                    disabled={!valid}
                    variant='outlined'
                    color='primary'
                    startIcon={<PlayArrowIcon />}
                    onClick={handleSave}
                  >Save</Button>
                </span>
              </Tooltip>
            }
          </Grid>
        )}
      </Grid>

      {access && (
        <ShareProjectDialog
          data-testid='dialog-share'
          open={openShareProject}
          onClose={handleCloseDialog}
        />
      )}
    </StyledPaper>
  )
}
